import type { Parser } from "../../tastyworks/deser"
import {
  EVENT_CONTRACT_CHAIN_DESER,
  EVENT_CONTRACT_CHAIN_LIST_DESER,
  EventContractChain,
  EventContractChainList,
} from "../../tastyworks/event-contract-chain"
import {
  EVENT_CONTRACT_INSTRUMENT_DESER,
  EventContractInstrument,
} from "../../tastyworks/event-contract-instrument"
import {
  EVENT_PRODUCT_DESER,
  EventProduct,
} from "../../tastyworks/event-product"
import type HttpClient from "../../tastyworks/http"
import type { Params } from "../../tastyworks/request"
import {
  parseItemsResponse,
  parseSingleResponse,
  type ItemsResponse,
  type SingleResponse,
} from "../../tastyworks/response"

export interface InstrumentTypeGroup {
  "instrument-type": string
  symbols: string[]
}

export interface EventProductsParams extends Params {
  exchange: string
  frequency?: string
}

function encodeSymbol(symbol: string): string {
  return encodeURIComponent(symbol).replace(/\./g, "%2E")
}

const INSTRUMENTS_API_VERSION = "20260415"
const INSTRUMENTS_API_HEADERS: HeadersInit = {
  "Accept-Version": INSTRUMENTS_API_VERSION,
}

export class InstrumentsService {
  constructor(private readonly baseHttpClient: HttpClient) {}

  readonly getEventProducts = async (
    params: EventProductsParams
  ): Promise<ItemsResponse<EventProduct>> =>
    this.baseHttpClient.indexSimple(
      EVENT_PRODUCT_DESER.toParser(EventProduct),
      "instruments/event-products",
      params,
      INSTRUMENTS_API_HEADERS
    )

  readonly getEventContractChain = async (
    symbol: string
  ): Promise<SingleResponse<EventContractChain>> => {
    const helper = await this.baseHttpClient.get(
      `instruments/event-contract-chains/${symbol}/nested`,
      undefined,
      INSTRUMENTS_API_HEADERS
    )
    return parseSingleResponse(
      helper,
      EVENT_CONTRACT_CHAIN_DESER.toParser(EventContractChain)
    )
  }

  readonly getEventContractChains = async (
    exchange: string,
    underlyingProduct: string
  ): Promise<SingleResponse<EventContractChainList>> => {
    const helper = await this.baseHttpClient.get(
      `instruments/event-contract-chains/${exchange}`,
      { "underlying-product": underlyingProduct },
      INSTRUMENTS_API_HEADERS
    )
    return parseSingleResponse(
      helper,
      EVENT_CONTRACT_CHAIN_LIST_DESER.toParser(EventContractChainList)
    )
  }

  readonly getEventContractChainsByProduct = async (
    exchange: string,
    eventProductExternalId: string
  ): Promise<SingleResponse<EventContractChainList>> => {
    const helper = await this.baseHttpClient.get(
      `instruments/event-contract-chains/${exchange}/${encodeURIComponent(eventProductExternalId)}`,
      undefined,
      INSTRUMENTS_API_HEADERS
    )
    return parseSingleResponse(
      helper,
      EVENT_CONTRACT_CHAIN_LIST_DESER.toParser(EventContractChainList)
    )
  }

  readonly show = async (
    symbol: string
  ): Promise<SingleResponse<EventContractInstrument>> =>
    this.baseHttpClient.show(
      new EventContractInstrument(),
      EVENT_CONTRACT_INSTRUMENT_DESER.update,
      `instruments/event-contracts/${encodeSymbol(symbol)}`,
      undefined,
      INSTRUMENTS_API_HEADERS
    )

  readonly search = async <T>(
    groups: InstrumentTypeGroup[],
    parser: Parser<T>,
    options?: { fields?: Record<string, string> }
  ): Promise<ItemsResponse<T>> => {
    const totalSymbols = groups.reduce(
      (sum, group) => sum + group.symbols.length,
      0
    )
    const helper = await this.baseHttpClient.postJson("instruments/search", {
      "page-offset": 0,
      "per-page": totalSymbols,
      "instrument-type-groups": groups,
      ...(options?.fields && { fields: options.fields }),
    })
    const unwrappedParser: Parser<T> = (itemHelper) => {
      const dataHelper = itemHelper.getChild("data")
      return parser(dataHelper)
    }
    return parseItemsResponse(helper, unwrappedParser)
  }
}
